import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { usePermissions } from '../../hooks/usePermissions';
import LanguageAndThemeToggle from '../Common/LanguageAndThemeToggle';
import CouponClaim from '../Coupon/CouponClaim';
import axios from 'axios';
import socketClient from '../../utils/socketClient';
import {
  AppBar,
  Toolbar,
  IconButton,
  Button,
  Menu,
  MenuItem,
  Stack,
  Box,
  Grid,
  Badge,
  Avatar,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Drawer,
  Chip,
  Tooltip,
  Typography,
  Paper,
  Collapse,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme as useMuiTheme } from '@mui/material/styles';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import GavelRoundedIcon from '@mui/icons-material/GavelRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import HistoryEduRoundedIcon from '@mui/icons-material/HistoryEduRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import MoveToInboxRoundedIcon from '@mui/icons-material/MoveToInboxRounded';
import BusinessCenterRoundedIcon from '@mui/icons-material/BusinessCenterRounded';
import FlashOnRoundedIcon from '@mui/icons-material/FlashOnRounded';
import RecyclingRoundedIcon from '@mui/icons-material/RecyclingRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import WalletRoundedIcon from '@mui/icons-material/WalletRounded';

const Navbar = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { isRTL } = useLanguage();
  const { logout, user } = useAuth();
  const { hasPermission } = usePermissions();
  const location = useLocation();
  const navigate = useNavigate();

  const muiTheme = useMuiTheme();
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('lg'));
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [roomsAnchorEl, setRoomsAnchorEl] = useState(null);
  const [managementAnchorEl, setManagementAnchorEl] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [notifAnchorEl, setNotifAnchorEl] = useState(null);
  const [walletAnchorEl, setWalletAnchorEl] = useState(null);
  const [moreAnchorEl, setMoreAnchorEl] = useState(null);
  const [isCouponModalOpen, setIsCouponModalOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [expandedSections, setExpandedSections] = useState({});

  const closeAllMenus = useCallback(() => {
    setRoomsAnchorEl(null);
    setManagementAnchorEl(null);
    setProfileAnchorEl(null);
    setNotifAnchorEl(null);
    setWalletAnchorEl(null);
    setMoreAnchorEl(null);
  }, []);

  useEffect(() => {
    if (!user) {
      setMobileOpen(false);
      closeAllMenus();
    }
  }, [user, closeAllMenus]);

  const resolveNotificationTarget = useCallback((notif) => {
    if (!notif) return '/notifications';

    const targetId = notif.relatedId;
    switch (notif.relatedModel) {
      case 'AuctionRoom':
        return targetId ? `/auction-room/${targetId}` : '/auction-room';
      case 'SecondHandProduct':
        return targetId ? `/ecommerce/product/${targetId}` : '/ecommerce';
      case 'SecondHandOffer':
        return targetId ? `/offers/${targetId}` : '/offers';
      case 'Product':
        return targetId ? `/ecommerce/product/${targetId}` : '/ecommerce';
      case 'Message':
        return '/offers/received';
      default:
        return '/notifications';
    }
  }, []);

  const handleNotificationClick = useCallback(
    async (notif) => {
      const notifId = notif._id || notif.id;
      const target = resolveNotificationTarget(notif);

      closeAllMenus();

      if (notifId && !notif.isRead) {
        try {
          const token = localStorage.getItem('token');
          await axios.put(
            `${process.env.REACT_APP_API_URL}/api/notifications/${notifId}/read`,
            {},
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          );
          setNotifications((prev) =>
            prev.map((n) => (n._id === notifId || n.id === notifId ? { ...n, isRead: true } : n))
          );
          setUnreadCount((prev) => Math.max(prev - 1, 0));
        } catch (err) {
          // Silently ignore read error to avoid blocking navigation
        }
      }

      navigate(target);
    },
    [closeAllMenus, navigate, resolveNotificationTarget]
  );

  useEffect(() => {
    if (!user) return;

    const fetchNotifications = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = res.data.data || [];
        setNotifications(list);
        setUnreadCount(list.filter((n) => !n.isRead).length);
      } catch (err) {
        setNotifications([]);
        setUnreadCount(0);
      }
    };

    fetchNotifications();

    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }

    const handleNew = (notif) => {
      setNotifications((prev) => [notif, ...prev]);
      setUnreadCount((prev) => prev + 1);
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          new Notification('New Notification', { body: notif.message });
        } catch (e) {}
      }
    };

    socketClient.on('notification_new', handleNew);
    return () => {
      socketClient.off('notification_new', handleNew);
    };
  }, [user]);

  const handleMarkAllRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/notifications/mark-all-read`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {}
  };

  const isActiveLink = (path) => location.pathname === path;

  const canAccess = useCallback(
    (requiredPermissions) => {
      if (!requiredPermissions || requiredPermissions.length === 0) return true;
      return requiredPermissions.some((p) => hasPermission(p));
    },
    [hasPermission]
  );

  const rawNavItems = useMemo(
    () => [
      { key: 'home', name: t('nav.home'), path: '/', icon: HomeRoundedIcon },
      {
        key: 'rooms',
        name: t('nav.rooms'),
        icon: GavelRoundedIcon,
        dropdown: [
          {
            key: 'bidRooms',
            name: t('nav.bidRooms'),
            path: '/rooms/bid',
            icon: FlashOnRoundedIcon,
          },
          {
            key: 'secondHand',
            name: t('nav.secondHand'),
            path: '/rooms/second-hand',
            icon: RecyclingRoundedIcon,
            requiredPermissions: ['view_products'],
          },
        ],
        inheritChildPermissions: true,
      },
      {
        key: 'ecommerce',
        name: t('nav.ecommerce'),
        path: '/ecommerce',
        icon: StorefrontRoundedIcon,
      },
      {
        key: 'management',
  name: t('nav.management'),
        icon: ManageAccountsRoundedIcon,
        dropdown: [
          {
            key: 'manageProducts',
            name: t('navbar.manageProducts', 'Products'),
            path: '/products/manage',
            icon: StorefrontRoundedIcon,
            requiredPermissions: ['manage_products'],
          },
          {
            key: 'manageAuctionRooms',
            name: t('navbar.manageAuctionRooms', 'Auction Rooms'),
            path: '/auction-rooms/manage',
            icon: GavelRoundedIcon,
            requiredPermissions: ['manage_auctions'],
          },
        ],
        inheritChildPermissions: true,
      },
      {
        key: 'adminDashboard',
  name: t('nav.adminDashboard'),
        path: '/admin',
        icon: ShieldRoundedIcon,
        requiredPermissions: ['system_admin'],
      },
    ],
    [t]
  );

  const navItems = useMemo(() => {
    return rawNavItems
      .map((item) => {
        if (item.dropdown) {
          const filteredChildren = (item.dropdown || []).filter((child) => canAccess(child.requiredPermissions));
          if (filteredChildren.length === 0) return null;
          return { ...item, dropdown: filteredChildren };
        }
        return canAccess(item.requiredPermissions) ? item : null;
      })
      .filter(Boolean);
  }, [rawNavItems, canAccess]);

  const highlightNavItems = useMemo(() => navItems.slice(0, 3), [navItems]);
  const overflowNavItems = useMemo(() => navItems.slice(3), [navItems]);
  const overflowMenuItems = useMemo(
    () =>
      overflowNavItems.flatMap((item) =>
        item.dropdown ? item.dropdown : item.path ? [item] : []
      ),
    [overflowNavItems]
  );

  const rawProfileItems = useMemo(
    () => [
      { key: 'profile', name: t('nav.profile'), path: '/profile', icon: AccountCircleRoundedIcon },
      { key: 'history', name: t('nav.history'), path: '/history', icon: HistoryEduRoundedIcon },
      {
        key: 'myOffers',
  name: t('nav.myOffers', 'My Offers'),
        path: '/offers/my-offers',
        icon: BusinessCenterRoundedIcon,
        requiredPermissions: ['view_products', 'place_bids', 'create_listings'],
      },
      {
        key: 'receivedOffers',
  name: t('nav.receivedOffers', 'Received Offers'),
        path: '/offers/received',
        icon: MoveToInboxRoundedIcon,
        requiredPermissions: ['view_products', 'create_listings'],
      },
  { key: 'notifications', name: t('nav.notifications'), path: '/notifications', icon: NotificationsNoneRoundedIcon },
      { key: 'settings', name: t('nav.settings'), path: '/settings', icon: SettingsRoundedIcon },
      {
        key: 'systemDashboard',
        name: t('nav.systemDashboard', 'System Dashboard'),
        path: '/admin',
        icon: ShieldRoundedIcon,
        requiredPermissions: ['manage_permissions', 'system_admin'],
      },
    ],
    [t]
  );

  const profileMenuItems = useMemo(
    () => rawProfileItems.filter((item) => canAccess(item.requiredPermissions)),
    [rawProfileItems, canAccess]
  );

  const quickActions = useMemo(
    () =>
      [
        {
          label: t('nav.bidRooms'),
          path: '/rooms/bid',
          icon: FlashOnRoundedIcon,
          requiredPermissions: ['view_auctions'],
        },
        {
          label: t('nav.secondHand'),
          path: '/rooms/second-hand',
          icon: RecyclingRoundedIcon,
          requiredPermissions: ['view_products'],
        },
        {
          label: t('nav.recoveryWallet'),
          path: '/recovery-wallet',
          icon: AccountBalanceWalletRoundedIcon,
        },
        {
          label: t('nav.profile'),
          path: '/profile',
          icon: AccountCircleRoundedIcon,
        },
      ].filter((action) => canAccess(action.requiredPermissions)),
    [canAccess, t]
  );

  const toggleSection = (key) => {
    setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderNavButton = (item) => {
    const Icon = item.icon;
    if (item.dropdown) {
      const isOpen =
        item.key === 'management'
          ? Boolean(managementAnchorEl)
          : item.key === 'rooms'
          ? Boolean(roomsAnchorEl)
          : false;
      const handleDropdownClick = (event) => {
        if (item.key === 'management') {
          setManagementAnchorEl(event.currentTarget);
        } else if (item.key === 'rooms') {
          setRoomsAnchorEl(event.currentTarget);
        }
      };
      return (
        <Button
          key={item.key}
          color="inherit"
          endIcon={
            <ExpandMoreRoundedIcon
              sx={{
                transition: 'transform 0.2s ease',
                transform: isOpen ? 'rotate(180deg)' : 'none',
              }}
            />
          }
          onClick={handleDropdownClick}
          sx={{
            borderRadius: 999,
            px: 1.8,
            py: 0.85,
            gap: 1,
            color: 'text.secondary',
            backgroundColor: 'transparent',
            '&:hover': {
              color: 'text.primary',
              backgroundColor: alpha(muiTheme.palette.primary.main, 0.09),
            },
          }}
        >
          <Icon fontSize="small" />
          <Typography variant="body2" fontWeight={600}>
            {item.name}
          </Typography>
        </Button>
      );
    }

    return (
      <Button
        key={item.key}
        component={Link}
        to={item.path}
        color="inherit"
        sx={{
          borderRadius: 999,
          px: 1.8,
          py: 0.85,
          gap: 1,
          fontWeight: 600,
          color: isActiveLink(item.path)
            ? 'text.primary'
            : 'text.secondary',
          backgroundColor: isActiveLink(item.path)
            ? alpha(muiTheme.palette.primary.main, 0.15)
            : 'transparent',
          border: '1px solid',
          borderColor: isActiveLink(item.path)
            ? alpha(muiTheme.palette.primary.main, 0.28)
            : 'transparent',
          '&:hover': {
            color: 'text.primary',
            backgroundColor: alpha(muiTheme.palette.primary.main, 0.1),
          },
        }}
        onClick={closeAllMenus}
      >
        <Icon fontSize="small" />
        <Typography variant="body2" fontWeight={600}>
          {item.name}
        </Typography>
      </Button>
    );
  };

  const renderMenuItems = (items) => (
    <Box sx={{ minWidth: 220 }}>
      {items.map((child) => {
        const Icon = child.icon;
        if (!child.path) return null;
        return (
          <MenuItem
            key={child.key || child.path}
            component={Link}
            to={child.path}
            onClick={() => {
              closeAllMenus();
              setMobileOpen(false);
            }}
          >
            <ListItemIcon>
              <Icon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={child.name}
              primaryTypographyProps={{ fontSize: 14, fontWeight: 500 }}
            />
          </MenuItem>
        );
      })}
    </Box>
  );

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          left: 0,
          right: 0,
          width: '100%',
          boxSizing: 'border-box',
          backdropFilter: 'blur(24px) saturate(145%)',
          WebkitBackdropFilter: 'blur(24px) saturate(145%)',
          borderBottom: `1px solid ${muiTheme.palette.divider}`,
          background: muiTheme.vitria?.nav || (isDarkMode ? 'rgba(8,7,19,.84)' : 'rgba(255,255,255,.86)'),
          boxShadow: isDarkMode ? '0 12px 42px rgba(0,0,0,.14)' : '0 12px 42px rgba(34,25,75,.045)',
          scrollbarGutter: 'stable',
        }}
      >
        <Toolbar sx={{ minHeight: 72, width: '100%', maxWidth: 1480, mx: 'auto', px: { xs: 2, md: 3.5 } }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ flexShrink: 0 }}
          >
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/logo192.png`}
                alt="Vitria"
                style={{
                  height: '36px',
                  width: 'auto',
                }}
              />
              <Typography
                component="span"
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  ml: 1.25,
                  color: 'text.primary',
                  fontSize: '0.78rem',
                  fontWeight: 800,
                  letterSpacing: '0.18em',
                }}
              >
                VITRIA
              </Typography>
            </Box>
          </Stack>

          {!isTablet && (
            <Stack
              direction={isRTL ? 'row-reverse' : 'row'}
              spacing={1}
              justifyContent="center"
              alignItems="center"
              sx={{ flexGrow: 1, mx: { md: 4, lg: 6 } }}
            >
              {highlightNavItems.map((item) => renderNavButton(item))}
              {overflowNavItems.length > 0 && (
                <Button
                  color="inherit"
                  onClick={(event) => setMoreAnchorEl(event.currentTarget)}
                  endIcon={
                    <ExpandMoreRoundedIcon
                      sx={{
                        transition: 'transform 0.2s ease',
                        transform: Boolean(moreAnchorEl) ? 'rotate(180deg)' : 'none',
                      }}
                    />
                  }
                  sx={{
                    borderRadius: 999,
                    px: 1.8,
                    py: 0.85,
                    gap: 1,
                    fontWeight: 600,
                    color: 'text.secondary',
                    backgroundColor: Boolean(moreAnchorEl) ? alpha(muiTheme.palette.primary.main, 0.14) : 'transparent',
                    '&:hover': {
                      color: 'text.primary',
                      backgroundColor: alpha(muiTheme.palette.primary.main, 0.1),
                    },
                  }}
                >
                  {t('nav.more', 'More')}
                </Button>
              )}
            </Stack>
          )}

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="flex-end"
            sx={{ flexGrow: isTablet ? 1 : 0 }}
          >
            {isTablet && user && (
              <Tooltip title={t('nav.menu', 'Menu')}>
                <IconButton
                  color="inherit"
                  onClick={() => setMobileOpen(true)}
                  aria-label={t('nav.menu', 'Menu')}
                  sx={{ borderRadius: 3 }}
                >
                  <MenuRoundedIcon />
                </IconButton>
              </Tooltip>
            )}

            {(!isMobile || !user) && (
              <Box sx={{ display: 'block' }}>
                <LanguageAndThemeToggle />
              </Box>
            )}

            {user && (
              <Tooltip title={t('nav.wallet', 'Wallet')}>
                <IconButton
                  color="inherit"
                  onClick={(event) => setWalletAnchorEl(event.currentTarget)}
                  sx={{
                    borderRadius: 3,
                    backgroundColor: alpha(muiTheme.palette.success.main, 0.12),
                    '&:hover': {
                      backgroundColor: alpha(muiTheme.palette.success.main, 0.2),
                    },
                  }}
                >
                  <WalletRoundedIcon />
                </IconButton>
              </Tooltip>
            )}

            {user && (
              <Tooltip title={t('nav.notifications', 'Notifications')}>
                <IconButton
                  color="inherit"
                  onClick={(event) => setNotifAnchorEl(event.currentTarget)}
                  sx={{
                    borderRadius: 3,
                    backgroundColor: alpha(muiTheme.palette.primary.main, 0.08),
                    '&:hover': {
                      backgroundColor: alpha(muiTheme.palette.primary.main, 0.16),
                    },
                  }}
                >
                  <Badge badgeContent={unreadCount} color="error">
                    <NotificationsNoneRoundedIcon />
                  </Badge>
                </IconButton>
              </Tooltip>
            )}

            {user ? (
              <Tooltip title={user.username}>
                <IconButton
                  color="inherit"
                  onClick={(event) => setProfileAnchorEl(event.currentTarget)}
                  sx={{
                    borderRadius: 3,
                    backgroundColor: alpha(muiTheme.palette.info.main, 0.08),
                    '&:hover': { backgroundColor: alpha(muiTheme.palette.info.main, 0.16) },
                  }}
                >
                  <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontWeight: 600 }}>
                    {user.username.charAt(0).toUpperCase()}
                  </Avatar>
                </IconButton>
              </Tooltip>
            ) : (
              <Button
                component={Link}
                to="/auth"
                variant="contained"
                disableElevation
                sx={{ minHeight: 42, borderRadius: 999, px: { xs: 2, md: 2.5 }, whiteSpace: 'nowrap' }}
              >
                {t('nav.login', 'Sign in')}
              </Button>
            )}
          </Stack>
        </Toolbar>

        {isMobile && (
          <Box
            component="nav"
            aria-label={t('nav.marketplaceNavigation', 'Marketplace navigation')}
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
              gap: 1,
              width: '100%',
              maxWidth: 560,
              mx: 'auto',
              px: 2,
              pb: 1.5,
            }}
          >
            <Button
              component={Link}
              to="/rooms/bid"
              variant={isActiveLink('/rooms/bid') ? 'contained' : 'outlined'}
              startIcon={<FlashOnRoundedIcon />}
              onClick={closeAllMenus}
              sx={{
                minWidth: 0,
                minHeight: 40,
                px: 1.5,
                borderColor: isActiveLink('/rooms/bid') ? 'transparent' : muiTheme.palette.divider,
                bgcolor: isActiveLink('/rooms/bid') ? undefined : alpha(muiTheme.palette.primary.main, 0.06),
                whiteSpace: 'nowrap',
              }}
            >
              {t('nav.bidRooms')}
            </Button>
            <Button
              component={Link}
              to="/ecommerce"
              variant={isActiveLink('/ecommerce') ? 'contained' : 'outlined'}
              startIcon={<StorefrontRoundedIcon />}
              onClick={closeAllMenus}
              sx={{
                minWidth: 0,
                minHeight: 40,
                px: 1.5,
                borderColor: isActiveLink('/ecommerce') ? 'transparent' : muiTheme.palette.divider,
                bgcolor: isActiveLink('/ecommerce') ? undefined : alpha(muiTheme.palette.primary.main, 0.06),
                whiteSpace: 'nowrap',
              }}
            >
              {t('nav.ecommerce')}
            </Button>
          </Box>
        )}
      </AppBar>

      <Menu
        anchorEl={roomsAnchorEl}
        open={Boolean(roomsAnchorEl)}
        onClose={() => setRoomsAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRTL ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: isRTL ? 'right' : 'left' }}
        disableScrollLock
      >
  {renderMenuItems(navItems.find((item) => item.key === 'rooms')?.dropdown || [])}
      </Menu>

      <Menu
        anchorEl={managementAnchorEl}
        open={Boolean(managementAnchorEl)}
        onClose={() => setManagementAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRTL ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: isRTL ? 'right' : 'left' }}
        disableScrollLock
      >
  {renderMenuItems(navItems.find((item) => item.key === 'management')?.dropdown || [])}
      </Menu>

      <Menu
        anchorEl={moreAnchorEl}
        open={Boolean(moreAnchorEl)}
        onClose={() => setMoreAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRTL ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: isRTL ? 'right' : 'left' }}
        disableScrollLock
      >
        {renderMenuItems(overflowMenuItems)}
      </Menu>

      <Menu
        anchorEl={profileAnchorEl}
        open={Boolean(profileAnchorEl)}
        onClose={() => setProfileAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 230, borderRadius: 1 } }}
        disableScrollLock
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {user?.username || t('navbar.guest', 'Guest')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email || ''}
          </Typography>
        </Box>
        <Divider />
        {profileMenuItems.map((item) => {
          const Icon = item.icon;
          return (
            <MenuItem
              key={item.key || item.path}
              component={Link}
              to={item.path}
              onClick={() => {
                setProfileAnchorEl(null);
                closeAllMenus();
              }}
            >
              <ListItemIcon>
                <Icon fontSize="small" />
              </ListItemIcon>
              <ListItemText>{item.name}</ListItemText>
            </MenuItem>
          );
        })}
        <MenuItem
          onClick={() => {
            setProfileAnchorEl(null);
            setIsCouponModalOpen(true);
          }}
        >
          <ListItemIcon>
            <LocalOfferRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('navbar.claimCoupon', 'Claim Coupon')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            logout();
            closeAllMenus();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <LogoutRoundedIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('nav.logout')}</ListItemText>
        </MenuItem>
      </Menu>

      <Menu
        anchorEl={notifAnchorEl}
        open={Boolean(notifAnchorEl)}
        onClose={() => setNotifAnchorEl(null)}
        PaperProps={{ sx: { width: 320, maxHeight: 420, borderRadius: 1 } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableScrollLock
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle1" fontWeight={600}>
              {t('nav.notifications', 'Notifications')}
            </Typography>
            <Button size="small" onClick={handleMarkAllRead} disabled={notifications.length === 0}>
              {t('navbar.markAllRead', 'Mark all read')}
            </Button>
          </Stack>
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
          {notifications.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body2">{t('navbar.noNotifications', 'No notifications yet')}</Typography>
            </Box>
          ) : (
            notifications.map((notif) => {
              const isRead = Boolean(notif.isRead);
              const notifId = notif._id || notif.id;
              return (
                <MenuItem
                  key={notifId}
                  onClick={() => handleNotificationClick(notif)}
                  sx={{
                    alignItems: 'flex-start',
                    py: 1.5,
                    px: 2,
                    gap: 1,
                    whiteSpace: 'normal',
                    backgroundColor: isRead ? 'transparent' : alpha(muiTheme.palette.warning.main, 0.12),
                    '&:hover': {
                      backgroundColor: alpha(muiTheme.palette.primary.main, 0.12),
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${muiTheme.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={isRead ? 500 : 600}>
                      {notif.message}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(notif.timestamp).toLocaleString()}
                    </Typography>
                  </Box>
                </MenuItem>
              );
            })
          )}
        </Box>
      </Menu>

      <Menu
        anchorEl={walletAnchorEl}
        open={Boolean(walletAnchorEl)}
        onClose={() => setWalletAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 260, borderRadius: 1 } }}
        disableScrollLock
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('navbar.yourWallet', 'Your wallet')}
          </Typography>
          <Stack spacing={1.5}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 1,
                backgroundColor: alpha(muiTheme.palette.primary.main, 0.08),
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <SavingsRoundedIcon fontSize="small" color="primary" />
                  <Typography variant="caption" color="text.secondary">
                    {t('navbar.mainTokens', 'Main TND')}
                  </Typography>
                </Stack>
                <Typography variant="subtitle1" fontWeight={700}>
                  {user?.tokenBalance ?? 0}
                </Typography>
              </Stack>
            </Paper>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 1,
                backgroundColor: alpha(muiTheme.palette.success.main, 0.08),
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center">
                  <AccountBalanceWalletRoundedIcon fontSize="small" color="success" />
                  <Typography variant="caption" color="text.secondary">
                    {t('nav.recoveryWallet', 'Recovery wallet')}
                  </Typography>
                </Stack>
                <Typography variant="subtitle1" fontWeight={700}>
                  {user?.recoveryBalance ?? 0}
                </Typography>
              </Stack>
              <Button
                size="small"
                component={Link}
                to="/recovery-wallet"
                sx={{ mt: 1.5 }}
                onClick={() => {
                  setWalletAnchorEl(null);
                  closeAllMenus();
                }}
              >
                {t('common.viewDetails')}
              </Button>
            </Paper>
          </Stack>
        </Box>
      </Menu>
      {user && (
        <Drawer
        anchor={isRTL ? 'right' : 'left'}
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        PaperProps={{
          sx: {
            width: 320,
            background: isDarkMode ? 'rgba(15,23,42,0.96)' : 'rgba(248,250,255,0.98)',
            backdropFilter: 'blur(12px)',
          },
        }}
        ModalProps={{ disableScrollLock: true }}
      >
        <Box sx={{ px: 2, py: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6" fontWeight={700}>
              {t('navbar.menu', 'Menu')}
            </Typography>
            <IconButton onClick={() => setMobileOpen(false)} aria-label={t('common.close')}>
              <CloseRoundedIcon />
            </IconButton>
          </Stack>
          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 3 }}>
            <LanguageAndThemeToggle />
          </Box>

          {user && (
            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                borderRadius: 3,
                background: alpha(muiTheme.palette.primary.main, 0.08),
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Avatar>
                  {(user?.username || 'U').charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {user?.username}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.email}
                  </Typography>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1} mt={2}>
                <Chip
                  icon={<SavingsRoundedIcon fontSize="small" />}
                  label={t('navbar.tokensChip', {
                    count: user?.tokenBalance ?? 0,
                  })}
                  sx={{ borderRadius: 3 }}
                />
                <Chip
                  icon={<AccountBalanceWalletRoundedIcon fontSize="small" />}
                  label={t('navbar.recoveryChip', {
                    count: user?.recoveryBalance ?? 0,
                  })}
                  component={Link}
                  to="/recovery-wallet"
                  clickable
                  sx={{ borderRadius: 3 }}
                  onClick={() => setMobileOpen(false)}
                />
              </Stack>
            </Paper>
          )}

          {quickActions.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                {t('navbar.quickLinks', 'Quick links')}
              </Typography>
              <Grid container spacing={1.5}>
                {quickActions.map((action) => {
                  const ActionIcon = action.icon;
                  return (
                    <Grid item xs={6} key={action.path}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<ActionIcon fontSize="small" />}
                        component={Link}
                        to={action.path}
                        onClick={() => {
                          setMobileOpen(false);
                          closeAllMenus();
                        }}
                        sx={{
                          justifyContent: 'flex-start',
                          borderRadius: 3,
                          textTransform: 'none',
                        }}
                      >
                        <Typography variant="body2" fontWeight={600}>
                          {action.label}
                        </Typography>
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          <List>
            {navItems.map((item) => {
              const Icon = item.icon;
              const hasDropdown = Boolean(item.dropdown);
              const isExpanded = expandedSections[item.key];

              if (hasDropdown) {
                return (
                  <Box key={item.key}>
                    <ListItemButton onClick={() => toggleSection(item.key)}>
                      <ListItemIcon>
                        <Icon />
                      </ListItemIcon>
                      <ListItemText primary={item.name} />
                      <ChevronRightRoundedIcon
                        sx={{
                          transition: 'transform 0.2s ease',
                          transform: isExpanded ? 'rotate(90deg)' : 'none',
                        }}
                      />
                    </ListItemButton>
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <List component="div" disablePadding>
                        {(item.dropdown || []).map((child) => {
                          const ChildIcon = child.icon;
                          return (
                            <ListItemButton
                              key={child.key || child.path}
                              component={Link}
                              to={child.path}
                              sx={{ pl: 6 }}
                              onClick={() => {
                                setMobileOpen(false);
                                closeAllMenus();
                              }}
                            >
                              <ListItemIcon>
                                <ChildIcon fontSize="small" />
                              </ListItemIcon>
                              <ListItemText primary={child.name} />
                            </ListItemButton>
                          );
                        })}
                      </List>
                    </Collapse>
                  </Box>
                );
              }

              return (
                <ListItemButton
                  key={item.key || item.path}
                  component={Link}
                  to={item.path}
                  onClick={() => {
                    setMobileOpen(false);
                    closeAllMenus();
                  }}
                  selected={isActiveLink(item.path)}
                >
                  <ListItemIcon>
                    <Icon />
                  </ListItemIcon>
                  <ListItemText primary={item.name} />
                </ListItemButton>
              );
            })}
          </List>

          <Divider sx={{ my: 2 }} />

          <List>
            {profileMenuItems.map((item) => {
              const Icon = item.icon;
              return (
                <ListItemButton
                  key={item.key || item.path}
                  component={Link}
                  to={item.path}
                  onClick={() => {
                    setMobileOpen(false);
                    closeAllMenus();
                  }}
                >
                  <ListItemIcon>
                    <Icon />
                  </ListItemIcon>
                  <ListItemText primary={item.name} />
                </ListItemButton>
              );
            })}
            <ListItemButton
              onClick={() => {
                setMobileOpen(false);
                setIsCouponModalOpen(true);
              }}
            >
              <ListItemIcon>
                <LocalOfferRoundedIcon />
              </ListItemIcon>
              <ListItemText primary={t('navbar.claimCoupon', 'Claim Coupon')} />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                logout();
                setMobileOpen(false);
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <LogoutRoundedIcon color="error" />
              </ListItemIcon>
              <ListItemText primary={t('nav.logout')} />
            </ListItemButton>
          </List>
        </Box>
        </Drawer>
      )}

      <CouponClaim isOpen={isCouponModalOpen} onClose={() => setIsCouponModalOpen(false)} />
    </>
  );
};

export default Navbar;
